import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ROLE_PERMISSIONS,
  UserRole,
  usePermissions,
} from "@/_core/hooks/usePermissions";
import { trpc } from "@/lib/trpc";
import { ShieldCheck } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { FormEvent, useMemo, useState } from "react";
import { toast } from "sonner";

const ROLES: UserRole[] = [
  "admin",
  "workspace_admin",
  "trainer",
  "user",
  "guardian",
  "editor",
];
const RELATIONSHIPS = ["self", "guardian", "equal"] as const;
type Relationship = (typeof RELATIONSHIPS)[number];

const ADDON_CATEGORIES = ["supplement", "service", "specialna", "masaz"];

function clientLabel(client?: {
  alias?: string | null;
  meno?: string | null;
  priezvisko?: string | null;
  email?: string | null;
}) {
  if (!client) return "Unknown client";
  return (
    client.alias ||
    [client.meno, client.priezvisko].filter(Boolean).join(" ") ||
    client.email ||
    "Unnamed client"
  );
}

function linkClientLabel(link: {
  klientAlias?: string | null;
  klientMeno?: string | null;
  klientPriezvisko?: string | null;
  klientEmail?: string | null;
}) {
  return clientLabel({
    alias: link.klientAlias,
    meno: link.klientMeno,
    priezvisko: link.klientPriezvisko,
    email: link.klientEmail,
  });
}

function userLabel(user?: {
  name?: string | null;
  email?: string | null;
  openId?: string | null;
}) {
  if (!user) return "Unknown user";
  return user.email || user.name || user.openId || "Unnamed user";
}

function activeBadge(active?: boolean | null) {
  return (
    <Badge variant={active === false ? "secondary" : "default"}>
      {active === false ? "Disabled" : "Active"}
    </Badge>
  );
}

export default function AdminManagementPage() {
  const permissions = usePermissions();
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  const usersQuery = trpc.admin.users.list.useQuery();
  const clientsQuery = trpc.klient.list.useQuery();
  const linksQuery = trpc.admin.userClientLinks.list.useQuery();
  const pantryGroupsQuery = trpc.admin.pantryGroups.list.useQuery();
  const treningQuery = trpc.admin.treningSluzby.list.useQuery();
  const doplnkyQuery = trpc.admin.doplnkoveSluzby.list.useQuery();
  const dochadzkaDoplnkyQuery = trpc.admin.dochadzkaDoplnky.list.useQuery();
  const kategoriaQuery = trpc.admin.kategoria.list.useQuery();
  const tagQuery = trpc.admin.tag.list.useQuery();
  const fpcQuery = trpc.admin.fpcTaxonomy.list.useQuery();
  const workspacesQuery = trpc.admin.workspace.list.useQuery();

  // ─── Workspace mutations ───────────────────────────────────────────────────
  const workspaceCreate = trpc.admin.workspace.create.useMutation({
    onSuccess: async () => {
      toast.success("Workspace created");
      await utils.admin.workspace.list.invalidate();
    },
    onError: e => toast.error(e.message),
  });
  const workspaceUpdate = trpc.admin.workspace.update.useMutation({
    onSuccess: async () => {
      toast.success("Workspace updated");
      await utils.admin.workspace.list.invalidate();
    },
    onError: e => toast.error(e.message),
  });
  const workspaceDelete = trpc.admin.workspace.delete.useMutation({
    onSuccess: async () => {
      toast.success("Workspace deleted");
      await utils.admin.workspace.list.invalidate();
    },
    onError: e => toast.error(e.message),
  });
  const assignUserWorkspace =
    trpc.admin.workspace.assignUserWorkspace.useMutation({
      onSuccess: async () => {
        toast.success("User workspace updated");
        await Promise.all([
          utils.admin.users.list.invalidate(),
          utils.admin.workspace.list.invalidate(),
        ]);
      },
      onError: e => toast.error(e.message),
    });
  const assignClientWorkspace =
    trpc.admin.workspace.assignClientWorkspace.useMutation({
      onSuccess: async () => {
        toast.success("Client workspace updated");
        await Promise.all([
          utils.klient.list.invalidate(),
          utils.admin.workspace.list.invalidate(),
        ]);
      },
      onError: e => toast.error(e.message),
    });

  // ─── User / link mutations ─────────────────────────────────────────────────
  const updateRole = trpc.admin.users.updateRole.useMutation({
    onSuccess: async () => {
      toast.success("User role updated");
      await utils.admin.users.list.invalidate();
    },
    onError: error => toast.error(error.message),
  });

  const upsertLink = trpc.admin.userClientLinks.upsert.useMutation({
    onSuccess: async () => {
      toast.success("User-client link saved");
      await Promise.all([
        utils.admin.userClientLinks.list.invalidate(),
        utils.admin.users.list.invalidate(),
      ]);
    },
    onError: error => toast.error(error.message),
  });
  const deleteLink = trpc.admin.userClientLinks.delete.useMutation({
    onSuccess: async () => {
      toast.success("User-client link deleted");
      await Promise.all([
        utils.admin.userClientLinks.list.invalidate(),
        utils.admin.users.list.invalidate(),
      ]);
    },
    onError: error => toast.error(error.message),
  });

  // ─── Pantry group mutations ────────────────────────────────────────────────
  const pantryCreate = trpc.admin.pantryGroups.create.useMutation({
    onSuccess: async () => {
      toast.success("Pantry group created");
      await utils.admin.pantryGroups.list.invalidate();
    },
    onError: error => toast.error(error.message),
  });
  const pantryUpdate = trpc.admin.pantryGroups.update.useMutation({
    onSuccess: async () => {
      toast.success("Pantry group updated");
      await utils.admin.pantryGroups.list.invalidate();
    },
    onError: error => toast.error(error.message),
  });
  const pantryDelete = trpc.admin.pantryGroups.delete.useMutation({
    onSuccess: async () => {
      toast.success("Pantry group deleted");
      await utils.admin.pantryGroups.list.invalidate();
    },
    onError: error => toast.error(error.message),
  });
  const pantryAddClient = trpc.admin.pantryGroups.addClient.useMutation({
    onSuccess: async () => {
      toast.success("Client added to pantry group");
      await utils.admin.pantryGroups.list.invalidate();
    },
    onError: error => toast.error(error.message),
  });
  const pantryRemoveClient = trpc.admin.pantryGroups.removeClient.useMutation({
    onSuccess: async () => {
      toast.success("Client removed from pantry group");
      await utils.admin.pantryGroups.list.invalidate();
    },
    onError: error => toast.error(error.message),
  });

  // ─── Attendance / service mutations ───────────────────────────────────────
  const trainingCreate = trpc.admin.treningSluzby.create.useMutation({
    onSuccess: () => invalidateAttendance("Training service created", utils),
  });
  const trainingUpdate = trpc.admin.treningSluzby.update.useMutation({
    onSuccess: () => invalidateAttendance("Training service updated", utils),
    onError: e => toast.error(e.message),
  });
  const trainingDelete = trpc.admin.treningSluzby.delete.useMutation({
    onSuccess: () => invalidateAttendance("Training service deleted", utils),
    onError: e => toast.error(e.message),
  });
  const addonCreate = trpc.admin.doplnkoveSluzby.create.useMutation({
    onSuccess: () => invalidateAttendance("Additional service created", utils),
    onError: e => toast.error(e.message),
  });
  const addonUpdate = trpc.admin.doplnkoveSluzby.update.useMutation({
    onSuccess: () => invalidateAttendance("Additional service updated", utils),
    onError: e => toast.error(e.message),
  });
  const addonDelete = trpc.admin.doplnkoveSluzby.delete.useMutation({
    onSuccess: () => invalidateAttendance("Additional service deleted", utils),
    onError: e => toast.error(e.message),
  });
  const attendanceAddonCreate = trpc.admin.dochadzkaDoplnky.create.useMutation({
    onSuccess: () =>
      invalidateAttendance("Attendance add-on row created", utils),
    onError: e => toast.error(e.message),
  });
  const attendanceAddonUpdate = trpc.admin.dochadzkaDoplnky.update.useMutation({
    onSuccess: () =>
      invalidateAttendance("Attendance add-on row updated", utils),
    onError: e => toast.error(e.message),
  });
  const attendanceAddonDelete = trpc.admin.dochadzkaDoplnky.delete.useMutation({
    onSuccess: () =>
      invalidateAttendance("Attendance add-on row deleted", utils),
    onError: e => toast.error(e.message),
  });

  // ─── Taxonomy mutations ────────────────────────────────────────────────────
  const categoryCreate = trpc.admin.kategoria.create.useMutation({
    onSuccess: () => invalidateTaxonomy("Category created", utils),
    onError: e => toast.error(e.message),
  });
  const categoryUpdate = trpc.admin.kategoria.update.useMutation({
    onSuccess: () => invalidateTaxonomy("Category updated", utils),
    onError: e => toast.error(e.message),
  });
  const categoryDelete = trpc.admin.kategoria.delete.useMutation({
    onSuccess: () => invalidateTaxonomy("Category deleted", utils),
    onError: e => toast.error(e.message),
  });
  const tagCreate = trpc.admin.tag.create.useMutation({
    onSuccess: () => invalidateTaxonomy("Tag created", utils),
    onError: e => toast.error(e.message),
  });
  const tagUpdate = trpc.admin.tag.update.useMutation({
    onSuccess: () => invalidateTaxonomy("Tag updated", utils),
    onError: e => toast.error(e.message),
  });
  const tagDelete = trpc.admin.tag.delete.useMutation({
    onSuccess: () => invalidateTaxonomy("Tag deleted", utils),
    onError: e => toast.error(e.message),
  });
  const fpcCreate = trpc.admin.fpcTaxonomy.create.useMutation({
    onSuccess: () => invalidateTaxonomy("FPC taxonomy row created", utils),
    onError: e => toast.error(e.message),
  });
  const fpcUpdate = trpc.admin.fpcTaxonomy.update.useMutation({
    onSuccess: () => invalidateTaxonomy("FPC taxonomy row updated", utils),
    onError: e => toast.error(e.message),
  });
  const fpcDelete = trpc.admin.fpcTaxonomy.delete.useMutation({
    onSuccess: () => invalidateTaxonomy("FPC taxonomy row deleted", utils),
    onError: e => toast.error(e.message),
  });

  // ─── Local form state ──────────────────────────────────────────────────────
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [linkForm, setLinkForm] = useState({
    userId: "",
    klientId: "",
    relationship: "self" as Relationship,
    isPrimary: false,
  });
  const [pantryName, setPantryName] = useState("");
  const [pantryClient, setPantryClient] = useState<Record<number, string>>({});
  const [trainingForm, setTrainingForm] = useState({
    pocetOsob: "1",
    trvanie: "60",
    nazov: "",
    cena: "0",
    poradie: "0",
    aktivna: true,
  });
  const [addonForm, setAddonForm] = useState({
    kategoria: "service",
    nazov: "",
    cena: "0",
    naklad: "0",
    poradie: "0",
    aktivna: true,
  });
  const [editingTrainingId, setEditingTrainingId] = useState<number | null>(
    null
  );
  const [trainingEditForm, setTrainingEditForm] = useState({
    nazov: "",
    cena: "",
  });
  const [editingAddonId, setEditingAddonId] = useState<number | null>(null);
  const [addonEditForm, setAddonEditForm] = useState({
    nazov: "",
    cena: "",
    naklad: "",
  });
  const [attendanceAddonForm, setAttendanceAddonForm] = useState({
    dochadzkaId: "",
    doplnokId: "",
    mnozstvo: "1",
    cenaVCaseVyberu: "0",
  });
  const [categoryName, setCategoryName] = useState("");
  const [tagForm, setTagForm] = useState({ nazov: "", farba: "" });
  const [fpcForm, setFpcForm] = useState({
    kod: "",
    uroven: "1",
    nazovSk: "",
    popis: "",
    rodicKod: "",
  });

  // ---------- accordion state for FPC taxonomy ----------
  const [expandedParents, setExpandedParents] = useState<Set<string>>(
    new Set()
  );

  const clients = clientsQuery.data ?? [];
  const users = usersQuery.data ?? [];
  const workspaces = workspacesQuery.data ?? [];

  const effectivePermissionRows = useMemo(
    () =>
      Object.entries(ROLE_PERMISSIONS).map(([role, rolePermissions]) => ({
        role,
        enabled: Object.entries(rolePermissions)
          .filter(([, value]) => value)
          .map(([key]) => key),
      })),
    []
  );

  if (!permissions.canAccessSystemAdmin) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("admin:adminAccessRequired")}</CardTitle>
            </CardHeader>
            <CardContent>
              {t("admin:thisPageIsAvailableOnlyToAdministrators")}
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 space-y-6">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {t("admin:adminManagement")}
            </h1>
            <p className="text-muted-foreground">
              {t("admin:adminManagement.description")}
            </p>
          </div>
        </div>

        <Tabs defaultValue="workspaces" className="space-y-4">
          <TabsList className="flex h-auto flex-wrap justify-start">
            <TabsTrigger value="workspaces">
              {t("admin:workspaces")}
            </TabsTrigger>
            <TabsTrigger value="users">
              {t("admin:usersAndPermissions")}
            </TabsTrigger>
            <TabsTrigger value="guardians">
              {t("admin:guardiansAndChildren")}
            </TabsTrigger>
            <TabsTrigger value="attendance">
              {t("admin:attendanceServices")}
            </TabsTrigger>
            <TabsTrigger value="taxonomy">
              {t("admin:foodAndContentTaxonomy")}
            </TabsTrigger>
          </TabsList>

          {/* ─── Workspaces tab ──────────────────────────────────────────── */}
          <TabsContent value="workspaces" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t("admin:workspaceManagement")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Create workspace */}
                <form
                  className="flex gap-2"
                  onSubmit={e => {
                    e.preventDefault();
                    if (newWorkspaceName.trim()) {
                      workspaceCreate.mutate({
                        nazov: newWorkspaceName.trim(),
                      });
                      setNewWorkspaceName("");
                    }
                  }}
                >
                  <Input
                    placeholder={t("admin:workspaceName")}
                    value={newWorkspaceName}
                    onChange={e => setNewWorkspaceName(e.target.value)}
                  />
                  <Button type="submit">{t("admin:createWorkspace")}</Button>
                </form>

                {/* Workspace list */}
                {workspaces.map(ws => (
                  <div key={ws.id} className="rounded-md border p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-lg">{ws.nazov}</div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const nazov = prompt(
                              t("admin:workspaceName"),
                              ws.nazov
                            );
                            if (nazov)
                              workspaceUpdate.mutate({ id: ws.id, nazov });
                          }}
                        >
                          {t("admin:rename")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            confirm(t("admin:deleteWorkspace") + "?") &&
                            workspaceDelete.mutate({ id: ws.id })
                          }
                        >
                          {t("admin:delete")}
                        </Button>
                      </div>
                    </div>

                    {/* Users in workspace */}
                    <div>
                      <div className="text-sm font-medium text-muted-foreground mb-1">
                        {t("admin:workspaceUsers")}
                      </div>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {users
                          .filter(u => u.workspaceId === ws.id)
                          .map(u => (
                            <Badge key={u.id} variant="secondary">
                              {userLabel(u)}{" "}
                              <span className="ml-1 text-xs opacity-60">
                                ({u.role})
                              </span>
                            </Badge>
                          ))}
                        {users.filter(u => u.workspaceId === ws.id).length ===
                          0 && (
                          <span className="text-sm text-muted-foreground">
                            —
                          </span>
                        )}
                      </div>
                      {/* Assign user to this workspace */}
                      <div className="flex gap-2">
                        <select
                          className="border rounded-md px-2 py-1 bg-background text-sm"
                          defaultValue=""
                          onChange={(
                            e: React.ChangeEvent<HTMLSelectElement>
                          ) => {
                            const userId = Number(e.target.value);
                            if (userId)
                              assignUserWorkspace.mutate({
                                userId,
                                workspaceId: ws.id,
                              });
                            e.target.value = "";
                          }}
                        >
                          <option value="">
                            {t("admin:assignWorkspace")} (user)
                          </option>
                          {users
                            .filter(u => u.workspaceId !== ws.id)
                            .map(u => (
                              <option key={u.id} value={u.id}>
                                {userLabel(u)}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>

                    {/* Clients in workspace */}
                    <div>
                      <div className="text-sm font-medium text-muted-foreground mb-1">
                        {t("admin:workspaceClients")}
                      </div>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {clients
                          .filter(c => c.workspaceId === ws.id)
                          .map(c => (
                            <Badge key={c.id} variant="outline">
                              {clientLabel(c)}
                            </Badge>
                          ))}
                        {clients.filter(c => c.workspaceId === ws.id).length ===
                          0 && (
                          <span className="text-sm text-muted-foreground">
                            —
                          </span>
                        )}
                      </div>
                      {/* Assign client to this workspace */}
                      <div className="flex gap-2">
                        <select
                          className="border rounded-md px-2 py-1 bg-background text-sm"
                          defaultValue=""
                          onChange={(
                            e: React.ChangeEvent<HTMLSelectElement>
                          ) => {
                            const klientId = Number(e.target.value);
                            if (klientId)
                              assignClientWorkspace.mutate({
                                klientId,
                                workspaceId: ws.id,
                              });
                            e.target.value = "";
                          }}
                        >
                          <option value="">
                            {t("admin:assignWorkspace")} (client)
                          </option>
                          {clients
                            .filter(c => c.workspaceId !== ws.id)
                            .map(c => (
                              <option key={c.id} value={c.id}>
                                {clientLabel(c)}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Unassigned users */}
                <div className="rounded-md border p-4 space-y-2">
                  <div className="font-semibold">
                    {t("admin:unassigned")} — {t("admin:workspaceUsers")}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {users
                      .filter(u => u.workspaceId == null)
                      .map(u => (
                        <Badge key={u.id} variant="secondary">
                          {userLabel(u)}{" "}
                          <span className="ml-1 text-xs opacity-60">
                            ({u.role})
                          </span>
                        </Badge>
                      ))}
                    {users.filter(u => u.workspaceId == null).length === 0 && (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── Users & permissions tab ─────────────────────────────────── */}
          <TabsContent value="users" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t("admin:userRoles")}</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("admin:user")}</TableHead>
                      <TableHead>{t("admin:workspace")}</TableHead>
                      <TableHead>{t("admin:role")}</TableHead>
                      <TableHead>{t("admin:linkedClients")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(user => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="font-medium">{userLabel(user)}</div>
                          <div className="text-xs text-muted-foreground">
                            ID {user.id}
                          </div>
                        </TableCell>
                        <TableCell>
                          <select
                            className="border rounded-md px-2 py-1 bg-background text-sm"
                            value={user.workspaceId ?? ""}
                            onChange={e =>
                              assignUserWorkspace.mutate({
                                userId: user.id,
                                workspaceId: e.target.value
                                  ? Number(e.target.value)
                                  : null,
                              })
                            }
                          >
                            <option value="">{t("admin:unassigned")}</option>
                            {workspaces.map(ws => (
                              <option key={ws.id} value={ws.id}>
                                {ws.nazov}
                              </option>
                            ))}
                          </select>
                        </TableCell>
                        <TableCell>
                          <select
                            className="border rounded-md px-2 py-1 bg-background"
                            value={user.role}
                            onChange={event =>
                              updateRole.mutate({
                                userId: user.id,
                                role: event.target.value as UserRole,
                              })
                            }
                          >
                            {ROLES.map(role => (
                              <option key={role} value={role}>
                                {role}
                              </option>
                            ))}
                          </select>
                        </TableCell>
                        <TableCell className="space-x-1">
                          {user.links.map(link => (
                            <Badge key={link.id} variant="outline">
                              {linkClientLabel(link)}
                            </Badge>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>{t("admin:effectiveRolePermissions")}</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2">
                {effectivePermissionRows.map(row => (
                  <div key={row.role} className="rounded-md border p-3">
                    <div className="font-semibold capitalize mb-2">
                      {row.role}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {row.enabled.map(permission => (
                        <Badge key={permission} variant="secondary">
                          {permission}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── Guardians / user-client links tab ───────────────────────── */}
          <TabsContent value="guardians" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t("admin:guardianChildLinks")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <form
                  className="grid gap-3 md:grid-cols-5"
                  onSubmit={event => {
                    event.preventDefault();
                    if (!linkForm.userId || !linkForm.klientId) return;
                    upsertLink.mutate({
                      userId: Number(linkForm.userId),
                      klientId: Number(linkForm.klientId),
                      relationship: linkForm.relationship,
                      isPrimary: linkForm.isPrimary,
                    });
                  }}
                >
                  <div>
                    <Label>{t("admin:user")}</Label>
                    <select
                      className="w-full border rounded-md px-2 py-2 bg-background"
                      value={linkForm.userId}
                      onChange={e =>
                        setLinkForm({ ...linkForm, userId: e.target.value })
                      }
                    >
                      <option value="">Select user</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {userLabel(user)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>{t("admin:childClient")}</Label>
                    <select
                      className="w-full border rounded-md px-2 py-2 bg-background"
                      value={linkForm.klientId}
                      onChange={e =>
                        setLinkForm({ ...linkForm, klientId: e.target.value })
                      }
                    >
                      <option value="">Select client</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {clientLabel(client)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>{t("admin:relationship")}</Label>
                    <select
                      className="w-full border rounded-md px-2 py-2 bg-background"
                      value={linkForm.relationship}
                      onChange={e =>
                        setLinkForm({
                          ...linkForm,
                          relationship: e.target.value as Relationship,
                        })
                      }
                    >
                      {RELATIONSHIPS.map(r => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="flex items-end gap-2 pb-2">
                    <Checkbox
                      checked={linkForm.isPrimary}
                      onCheckedChange={checked =>
                        setLinkForm({
                          ...linkForm,
                          isPrimary: checked === true,
                        })
                      }
                    />{" "}
                    {t("admin:primary")}
                  </label>
                  <Button type="submit" className="self-end">
                    {t("admin:save")}
                  </Button>
                </form>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("admin:user")}</TableHead>
                      <TableHead>{t("admin:childClient")}</TableHead>
                      <TableHead>{t("admin:relationship")}</TableHead>
                      <TableHead>{t("admin:flags")}</TableHead>
                      <TableHead>{t("admin:actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(linksQuery.data ?? []).map(link => (
                      <TableRow key={link.id}>
                        <TableCell>
                          {link.userEmail || link.userName || link.userId}
                        </TableCell>
                        <TableCell>{linkClientLabel(link)}</TableCell>
                        <TableCell>{link.relationship}</TableCell>
                        <TableCell className="space-x-1">
                          {link.isPrimary && <Badge>Primary</Badge>}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              confirm(t("admin:deleteGuardianChildLink")) &&
                              deleteLink.mutate({ id: link.id })
                            }
                          >
                            {t("admin:delete")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>{t("admin:pantryGroups")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <form
                  className="flex gap-2"
                  onSubmit={event => {
                    event.preventDefault();
                    if (pantryName.trim())
                      pantryCreate.mutate({ nazov: pantryName.trim() });
                    setPantryName("");
                  }}
                >
                  <Input
                    placeholder={t("admin:newHouseholdPantryGroup")}
                    value={pantryName}
                    onChange={e => setPantryName(e.target.value)}
                  />
                  <Button type="submit">{t("admin:createGroup")}</Button>
                </form>
                {(pantryGroupsQuery.data ?? []).map(group => (
                  <div
                    key={group.id}
                    className="rounded-md border p-3 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-semibold">{group.nazov}</div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const nazov = prompt("Group name", group.nazov);
                            if (nazov)
                              pantryUpdate.mutate({ id: group.id, nazov });
                          }}
                        >
                          {t("admin:rename")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            confirm("Delete this pantry group?") &&
                            pantryDelete.mutate({ id: group.id })
                          }
                        >
                          {t("admin:delete")}
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {group.clients.map(member => (
                        <Badge
                          key={member.id}
                          variant="secondary"
                          className="gap-2"
                        >
                          {linkClientLabel(member)}{" "}
                          <button
                            onClick={() =>
                              pantryRemoveClient.mutate({ id: member.id })
                            }
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <select
                        className="border rounded-md px-2 py-2 bg-background"
                        value={pantryClient[group.id] ?? ""}
                        onChange={e =>
                          setPantryClient({
                            ...pantryClient,
                            [group.id]: e.target.value,
                          })
                        }
                      >
                        <option value="">{t("admin:addClient")}</option>
                        {clients.map(client => (
                          <option key={client.id} value={client.id}>
                            {clientLabel(client)}
                          </option>
                        ))}
                      </select>
                      <Button
                        variant="outline"
                        onClick={() =>
                          pantryClient[group.id] &&
                          pantryAddClient.mutate({
                            pantryGroupId: group.id,
                            klientId: Number(pantryClient[group.id]),
                          })
                        }
                      >
                        {t("common:actions.add")}
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── Attendance services tab ──────────────────────────────────── */}
          <TabsContent value="attendance" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t("admin:trainingServices")}</CardTitle>
              </CardHeader>
              <CardContent>
                <form
                  className="grid gap-2 md:grid-cols-6 mb-4"
                  onSubmit={e => {
                    e.preventDefault();
                    trainingCreate.mutate({
                      ...trainingForm,
                      poradie: Number(trainingForm.poradie),
                      aktivna: trainingForm.aktivna,
                    });
                  }}
                >
                  <Input
                    placeholder={t("admin:name")}
                    value={trainingForm.nazov}
                    onChange={e =>
                      setTrainingForm({
                        ...trainingForm,
                        nazov: e.target.value,
                      })
                    }
                  />
                  <Input
                    placeholder={t("admin:people")}
                    value={trainingForm.pocetOsob}
                    onChange={e =>
                      setTrainingForm({
                        ...trainingForm,
                        pocetOsob: e.target.value,
                      })
                    }
                  />
                  <Input
                    placeholder={t("admin:duration")}
                    value={trainingForm.trvanie}
                    onChange={e =>
                      setTrainingForm({
                        ...trainingForm,
                        trvanie: e.target.value,
                      })
                    }
                  />
                  <Input
                    placeholder={t("admin:price")}
                    value={trainingForm.cena}
                    onChange={e =>
                      setTrainingForm({ ...trainingForm, cena: e.target.value })
                    }
                  />
                  <Input
                    placeholder={t("admin:order")}
                    value={trainingForm.poradie}
                    onChange={e =>
                      setTrainingForm({
                        ...trainingForm,
                        poradie: e.target.value,
                      })
                    }
                  />
                  <Button type="submit">{t("admin:create")}</Button>
                </form>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("admin:name")}</TableHead>
                      <TableHead>{t("admin:people")}</TableHead>
                      <TableHead>{t("admin:duration")}</TableHead>
                      <TableHead>{t("admin:price")}</TableHead>
                      <TableHead>{t("admin:status")}</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(treningQuery.data ?? []).map(row => (
                      <TableRow key={row.id}>
                        <TableCell>
                          {editingTrainingId === row.id ? (
                            <Input
                              aria-label={`${t("admin:name")} ${row.nazov}`}
                              value={trainingEditForm.nazov}
                              onChange={event =>
                                setTrainingEditForm({
                                  ...trainingEditForm,
                                  nazov: event.target.value,
                                })
                              }
                              disabled={trainingUpdate.isPending}
                            />
                          ) : (
                            row.nazov
                          )}
                        </TableCell>
                        <TableCell>{row.pocetOsob}</TableCell>
                        <TableCell>{row.trvanie}</TableCell>
                        <TableCell>
                          {editingTrainingId === row.id ? (
                            <Input
                              aria-label={`${t("admin:price")} ${row.nazov}`}
                              type="number"
                              min="0"
                              step="0.01"
                              inputMode="decimal"
                              value={trainingEditForm.cena}
                              onChange={event =>
                                setTrainingEditForm({
                                  ...trainingEditForm,
                                  cena: event.target.value,
                                })
                              }
                              disabled={trainingUpdate.isPending}
                            />
                          ) : (
                            row.cena
                          )}
                        </TableCell>
                        <TableCell>{activeBadge(row.aktivna)}</TableCell>
                        <TableCell className="space-x-2">
                          {editingTrainingId === row.id ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={
                                  trainingUpdate.isPending ||
                                  !trainingEditForm.nazov ||
                                  !trainingEditForm.cena.trim()
                                }
                                onClick={async () => {
                                  try {
                                    await trainingUpdate.mutateAsync({
                                      id: row.id,
                                      data: {
                                        nazov: trainingEditForm.nazov,
                                        cena: trainingEditForm.cena,
                                      },
                                    });
                                    setEditingTrainingId(null);
                                  } catch {
                                    // The mutation's onError handler displays the failure.
                                  }
                                }}
                              >
                                {t("admin:save")}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={trainingUpdate.isPending}
                                onClick={() => setEditingTrainingId(null)}
                              >
                                {t("admin:cancel")}
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingTrainingId(row.id);
                                setTrainingEditForm({
                                  nazov: row.nazov,
                                  cena: row.cena,
                                });
                              }}
                            >
                              {t("admin:edit")}
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              trainingUpdate.mutate({
                                id: row.id,
                                data: { aktivna: row.aktivna === false },
                              })
                            }
                          >
                            {row.aktivna === false
                              ? t("admin:enable")
                              : t("admin:disable")}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              confirm("Delete training service?") &&
                              trainingDelete.mutate({ id: row.id })
                            }
                          >
                            {t("admin:delete")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>
                  {t("admin:additionalServicesAndSupplements")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form
                  className="grid gap-2 md:grid-cols-7 mb-4"
                  onSubmit={e => {
                    e.preventDefault();
                    addonCreate.mutate({
                      ...addonForm,
                      poradie: Number(addonForm.poradie),
                      aktivna: addonForm.aktivna,
                    });
                  }}
                >
                  <select
                    className="border rounded-md px-2 py-2 bg-background"
                    value={addonForm.kategoria}
                    onChange={e =>
                      setAddonForm({ ...addonForm, kategoria: e.target.value })
                    }
                  >
                    {ADDON_CATEGORIES.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  <Input
                    placeholder={t("admin:name")}
                    value={addonForm.nazov}
                    onChange={e =>
                      setAddonForm({ ...addonForm, nazov: e.target.value })
                    }
                  />
                  <Input
                    placeholder={t("admin:price")}
                    value={addonForm.cena}
                    onChange={e =>
                      setAddonForm({ ...addonForm, cena: e.target.value })
                    }
                  />
                  <Input
                    placeholder={t("admin:productCost")}
                    value={addonForm.naklad}
                    onChange={e =>
                      setAddonForm({ ...addonForm, naklad: e.target.value })
                    }
                  />
                  <Input
                    placeholder={t("admin:order")}
                    value={addonForm.poradie}
                    onChange={e =>
                      setAddonForm({ ...addonForm, poradie: e.target.value })
                    }
                  />
                  <Button type="submit">{t("admin:create")}</Button>
                </form>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("admin:category")}</TableHead>
                      <TableHead>{t("admin:name")}</TableHead>
                      <TableHead>{t("admin:price")}</TableHead>
                      <TableHead>{t("admin:productCost")}</TableHead>
                      <TableHead>{t("admin:status")}</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(doplnkyQuery.data ?? []).map(row => (
                      <TableRow key={row.id}>
                        <TableCell>{row.kategoria}</TableCell>
                        <TableCell>
                          {editingAddonId === row.id ? (
                            <Input
                              aria-label={`${t("admin:name")} ${row.nazov}`}
                              value={addonEditForm.nazov}
                              onChange={event =>
                                setAddonEditForm({
                                  ...addonEditForm,
                                  nazov: event.target.value,
                                })
                              }
                              disabled={addonUpdate.isPending}
                            />
                          ) : (
                            row.nazov
                          )}
                        </TableCell>
                        <TableCell>
                          {editingAddonId === row.id ? (
                            <Input
                              aria-label={`${t("admin:price")} ${row.nazov}`}
                              type="number"
                              min="0"
                              step="0.01"
                              inputMode="decimal"
                              value={addonEditForm.cena}
                              onChange={event =>
                                setAddonEditForm({
                                  ...addonEditForm,
                                  cena: event.target.value,
                                })
                              }
                              disabled={addonUpdate.isPending}
                            />
                          ) : (
                            row.cena
                          )}
                        </TableCell>
                        <TableCell>
                          {editingAddonId === row.id ? (
                            <Input
                              aria-label={`${t("admin:productCost")} ${row.nazov}`}
                              type="number"
                              min="0"
                              step="0.01"
                              inputMode="decimal"
                              value={addonEditForm.naklad}
                              onChange={event =>
                                setAddonEditForm({
                                  ...addonEditForm,
                                  naklad: event.target.value,
                                })
                              }
                              disabled={addonUpdate.isPending}
                            />
                          ) : (
                            row.naklad
                          )}
                        </TableCell>
                        <TableCell>{activeBadge(row.aktivna)}</TableCell>
                        <TableCell className="space-x-2">
                          {editingAddonId === row.id ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={
                                  addonUpdate.isPending ||
                                  !addonEditForm.nazov ||
                                  !addonEditForm.cena.trim() ||
                                  !addonEditForm.naklad.trim()
                                }
                                onClick={async () => {
                                  try {
                                    await addonUpdate.mutateAsync({
                                      id: row.id,
                                      data: {
                                        nazov: addonEditForm.nazov,
                                        cena: addonEditForm.cena,
                                        naklad: addonEditForm.naklad,
                                      },
                                    });
                                    setEditingAddonId(null);
                                  } catch {
                                    // The mutation's onError handler displays the failure.
                                  }
                                }}
                              >
                                {t("admin:save")}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={addonUpdate.isPending}
                                onClick={() => setEditingAddonId(null)}
                              >
                                {t("admin:cancel")}
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingAddonId(row.id);
                                setAddonEditForm({
                                  nazov: row.nazov,
                                  cena: row.cena,
                                  naklad: row.naklad,
                                });
                              }}
                            >
                              {t("admin:edit")}
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              addonUpdate.mutate({
                                id: row.id,
                                data: { aktivna: row.aktivna === false },
                              })
                            }
                          >
                            {row.aktivna === false
                              ? t("admin:enable")
                              : t("admin:disable")}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              confirm("Delete additional service?") &&
                              addonDelete.mutate({ id: row.id })
                            }
                          >
                            {t("admin:delete")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── Food & content taxonomy tab ─────────────────────────────── */}
          <TabsContent value="taxonomy" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t("foodCategories")}</CardTitle>
              </CardHeader>
              <CardContent>
                <form
                  className="flex gap-2 mb-4"
                  onSubmit={e => {
                    e.preventDefault();
                    if (categoryName.trim())
                      categoryCreate.mutate({
                        kategoria: categoryName.trim(),
                        aktivna: true,
                      });
                    setCategoryName("");
                  }}
                >
                  <Input
                    placeholder={t("admin:category")}
                    value={categoryName}
                    onChange={e => setCategoryName(e.target.value)}
                  />
                  <Button type="submit">{t("admin:create")}</Button>
                </form>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(kategoriaQuery.data ?? []).map(row => (
                      <TableRow key={row.id}>
                        <TableCell>{row.kategoria}</TableCell>
                        <TableCell>{activeBadge(row.aktivna)}</TableCell>
                        <TableCell className="space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const kategoria = prompt(
                                "Category name",
                                row.kategoria
                              );
                              if (kategoria)
                                categoryUpdate.mutate({
                                  id: row.id,
                                  data: { kategoria },
                                });
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              categoryUpdate.mutate({
                                id: row.id,
                                data: { aktivna: row.aktivna === false },
                              })
                            }
                          >
                            {row.aktivna === false ? "Enable" : "Disable"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              confirm(t("admin:deleteCategory")) &&
                              categoryDelete.mutate({ id: row.id })
                            }
                          >
                            {t("admin:delete")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Tags</CardTitle>
              </CardHeader>
              <CardContent>
                <form
                  className="grid gap-2 md:grid-cols-3 mb-4"
                  onSubmit={e => {
                    e.preventDefault();
                    if (tagForm.nazov.trim())
                      tagCreate.mutate({
                        nazov: tagForm.nazov.trim(),
                        farba: tagForm.farba || null,
                        aktivna: true,
                      });
                  }}
                >
                  <Input
                    placeholder={t("admin:tagName")}
                    value={tagForm.nazov}
                    onChange={e =>
                      setTagForm({ ...tagForm, nazov: e.target.value })
                    }
                  />
                  <Input
                    placeholder={t("admin:tagColor")}
                    value={tagForm.farba}
                    onChange={e =>
                      setTagForm({ ...tagForm, farba: e.target.value })
                    }
                  />
                  <Button type="submit">{t("admin:create")}</Button>
                </form>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Color</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(tagQuery.data ?? []).map(row => (
                      <TableRow key={row.id}>
                        <TableCell>{row.nazov}</TableCell>
                        <TableCell>{row.farba}</TableCell>
                        <TableCell>{activeBadge(row.aktivna)}</TableCell>
                        <TableCell className="space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const nazov = prompt("Tag name", row.nazov);
                              if (nazov)
                                tagUpdate.mutate({
                                  id: row.id,
                                  data: { nazov },
                                });
                            }}
                          >
                            {t("edit")}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              tagUpdate.mutate({
                                id: row.id,
                                data: { aktivna: row.aktivna === false },
                              })
                            }
                          >
                            {row.aktivna === false ? t("enable") : t("disable")}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              confirm(t("admin:deleteTag")) &&
                              tagDelete.mutate({ id: row.id })
                            }
                          >
                            {t("admin:delete")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>FPC taxonomy</CardTitle>
              </CardHeader>
              <CardContent>
                <form
                  className="grid gap-2 md:grid-cols-6 mb-4"
                  onSubmit={(e: FormEvent) => {
                    e.preventDefault();
                    fpcCreate.mutate({
                      kod: fpcForm.kod,
                      uroven: Number(fpcForm.uroven),
                      nazovSk: fpcForm.nazovSk,
                      popis: fpcForm.popis || null,
                      rodicKod: fpcForm.rodicKod || null,
                      aktivna: true,
                    });
                  }}
                >
                  <Input
                    placeholder={t("admin:code")}
                    value={fpcForm.kod}
                    onChange={e =>
                      setFpcForm({ ...fpcForm, kod: e.target.value })
                    }
                  />
                  <Input
                    placeholder={t("admin:level")}
                    value={fpcForm.uroven}
                    onChange={e =>
                      setFpcForm({ ...fpcForm, uroven: e.target.value })
                    }
                  />
                  <Input
                    placeholder={t("admin:name")}
                    value={fpcForm.nazovSk}
                    onChange={e =>
                      setFpcForm({ ...fpcForm, nazovSk: e.target.value })
                    }
                  />
                  <Input
                    placeholder={t("admin:description")}
                    value={fpcForm.popis}
                    onChange={e =>
                      setFpcForm({ ...fpcForm, popis: e.target.value })
                    }
                  />
                  <Input
                    placeholder={t("admin:parent")}
                    value={fpcForm.rodicKod}
                    onChange={e =>
                      setFpcForm({ ...fpcForm, rodicKod: e.target.value })
                    }
                  />
                  <Button type="submit">{t("admin:create")}</Button>
                </form>
                <div className="flex justify-end gap-2 mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setExpandedParents(new Set());
                    }}
                  >
                    {t("admin:collapseAll")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const allParents = new Set(
                        (fpcQuery.data ?? [])
                          .filter(r =>
                            (fpcQuery.data ?? []).some(
                              row => row.rodicKod === r.kod
                            )
                          )
                          .map(r => r.kod)
                      );
                      setExpandedParents(allParents);
                    }}
                  >
                    {t("admin:expandAll")}
                  </Button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("admin:code")}</TableHead>
                      <TableHead>{t("admin:level")}</TableHead>
                      <TableHead>{t("admin:name")}</TableHead>
                      <TableHead>{t("admin:parent")}</TableHead>
                      <TableHead>{t("admin:status")}</TableHead>
                      <TableHead>{t("admin:actions")}</TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {(() => {
                      const renderRow = (row: any, level = 0) => (
                        <TableRow
                          key={row.kod}
                          className={level > 0 ? "bg-muted/50" : ""}
                        >
                          <TableCell className="flex items-center gap-2">
                            {(fpcQuery.data ?? []).filter(
                              r => r.rodicKod === row.kod
                            ).length > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setExpandedParents(prev => {
                                    const set = new Set(prev);
                                    if (set.has(row.kod)) set.delete(row.kod);
                                    else set.add(row.kod);
                                    return set;
                                  });
                                }}
                              >
                                {expandedParents.has(row.kod) ? "▾" : "▸"}
                              </Button>
                            )}
                            {(fpcQuery.data ?? []).filter(
                              r => r.rodicKod === row.kod
                            ).length === 0 && <span>-</span>}
                            <span className={level > 0 ? "ml-4" : ""}>
                              {row.kod}
                            </span>
                          </TableCell>
                          <TableCell>{row.uroven}</TableCell>
                          <TableCell>{row.nazovSk}</TableCell>
                          <TableCell>{row.rodicKod || "-"}</TableCell>
                          <TableCell>{activeBadge(row.aktivna)}</TableCell>
                          <TableCell className="space-x-2">
                            <Button variant="outline" size="sm">
                              {t("admin:edit")}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                fpcUpdate.mutate({
                                  kod: row.kod,
                                  data: { aktivna: !row.aktivna },
                                });
                              }}
                            >
                              {row.aktivna
                                ? t("admin:disable")
                                : t("admin:enable")}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() =>
                                confirm(t("admin:deleteFpcRow")) &&
                                fpcDelete.mutate({ kod: row.kod })
                              }
                            >
                              {t("admin:delete")}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );

                      const renderTree = (
                        parentCode: string | null = null,
                        level = 0
                      ): any => {
                        const rows = (fpcQuery.data ?? [])
                          .filter(r => r.rodicKod === parentCode)
                          .sort((a, b) => a.kod.localeCompare(b.kod));

                        return rows.flatMap((row: any) => [
                          renderRow(row, level),
                          ...(expandedParents.has(row.kod)
                            ? renderTree(row.kod, level + 1)
                            : []),
                        ]);
                      };

                      return renderTree();
                    })()}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

function invalidateAttendance(
  message: string,
  utils: ReturnType<typeof trpc.useUtils>
) {
  toast.success(message);
  void Promise.all([
    utils.admin.treningSluzby.list.invalidate(),
    utils.admin.doplnkoveSluzby.list.invalidate(),
    utils.admin.dochadzkaDoplnky.list.invalidate(),
  ]);
}

function invalidateTaxonomy(
  message: string,
  utils: ReturnType<typeof trpc.useUtils>
) {
  toast.success(message);
  void Promise.all([
    utils.admin.kategoria.list.invalidate(),
    utils.admin.tag.list.invalidate(),
    utils.admin.fpcTaxonomy.list.invalidate(),
  ]);
}
